import { useCallback, useMemo } from 'react';
import { useRxData } from 'rxdb-hooks';

import { RxdbCollectionName } from '../rxdbCollectionName';
import type {
  DeficiencyComments,
  DeficiencyCommentsCollection,
  DeficiencyVersionCommentsCollection,
} from './deficiencyComments.types';

const bindCommentsForActiveDeficiency = (
  oldVersionComments: DeficiencyComments[],
  newVersionComments: DeficiencyComments[],
) => {
  let deficiencyIds = [
    ...new Set(newVersionComments.map(comments => comments.fkAssetInspectionComponentFailure)),
  ];
  deficiencyIds = [
    ...deficiencyIds,
    ...new Set(oldVersionComments.map(comments => comments.fkAssetInspectionComponentFailure)),
  ];
  let comments: DeficiencyComments[] = [];
  deficiencyIds.forEach(id => {
    const versionComments = newVersionComments.filter(
      x => x.fkAssetInspectionComponentFailure === id,
    );
    const existinCommentIds = [
      ...new Set(comments.map(deficiency => deficiency.fkAssetInspectionComponentFailure)),
    ];
    if (!existinCommentIds.includes(id)) {
      if (versionComments?.length) {
        comments = [...comments, ...versionComments];
      } else {
        const previousComments = oldVersionComments.filter(
          x => x.fkAssetInspectionComponentFailure === id,
        );
        comments = [...comments, ...previousComments];
      }
    }
  });
  return comments;
};

const useDeficiencyWithVersionComments = (
  deficiencyId: number,
  componentVersionId: string,
  isActiveDeficiency: boolean,
  inspectionId: number,
) => {
  const getActiveDeficiencyCommentsQuery = useCallback(
    (collection: DeficiencyVersionCommentsCollection) =>
      collection
        ?.find({
          selector: {
            $and: [
              {
                fkAssetInspectionComponentFailure: deficiencyId,
                componentVersionId,
                componentVersionInspectionId: inspectionId,
              },
            ],
          },
        })
        .sort({
          dateCreated: `desc`,
        }),
    [componentVersionId, deficiencyId, inspectionId],
  );

  const getDeficiencyCommentsQuery = useCallback(
    (collection: DeficiencyCommentsCollection) =>
      collection
        ?.find({
          selector: {
            $and: [{ fkAssetInspectionComponentFailure: deficiencyId }],
          },
        })
        .sort({
          dateCreated: `desc`,
        }),
    [deficiencyId],
  );

  const getSelectedDeficiencyComments = useCallback(
    (collection: DeficiencyCommentsCollection) =>
      collection
        ?.find({
          selector: {
            $and: [{ fkAssetInspectionComponentFailure: deficiencyId }],
          },
        })
        .sort({
          dateCreated: `desc`,
        }),
    [deficiencyId],
  );

  const getSelectedActiveDeficiencyComments = useCallback(
    (collection: DeficiencyCommentsCollection) =>
      collection
        ?.find({
          selector: {
            $and: [
              {
                fkAssetInspectionComponentFailure: deficiencyId,
                componentVersionId,
                componentVersionInspectionId: inspectionId,
                isVersionComment: true,
              },
            ],
          },
        })
        .sort({
          dateCreated: `desc`,
        }),
    [deficiencyId, componentVersionId, inspectionId],
  );

  const { result: activeDeficiencyComments } = useRxData<DeficiencyComments>(
    RxdbCollectionName.DEFICIENCY_VERSION_COMMENTS,
    getActiveDeficiencyCommentsQuery,
    { json: true },
  );

  const { result: deficiencyComments } = useRxData<DeficiencyComments>(
    RxdbCollectionName.DEFICIENCY_COMMENTS,
    getDeficiencyCommentsQuery,
    { json: true },
  );

  const { result: selectedDeficiencyComments } = useRxData<DeficiencyComments>(
    RxdbCollectionName.DEFICIENCY_COMMENTS,
    getSelectedDeficiencyComments,
    { json: true },
  );

  const { result: selectedActiveDeficiencyComments } = useRxData<DeficiencyComments>(
    RxdbCollectionName.DEFICIENCY_VERSION_COMMENTS,
    getSelectedActiveDeficiencyComments,
    { json: true },
  );
  const previousDeficiencyComments = useMemo(() => {
    return isActiveDeficiency
      ? bindCommentsForActiveDeficiency(deficiencyComments, activeDeficiencyComments)
      : deficiencyComments;
  }, [isActiveDeficiency, deficiencyComments, activeDeficiencyComments]);

  return {
    previousDeficiencyComments,
    selectedDeficiencyComments,
    selectedActiveDeficiencyComments,
  };
};
export default useDeficiencyWithVersionComments;
